import React from 'react';
import { Pencil, Plus, TabletSmartphone, Trash2, Unplug, UserPlus } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { Chip } from '../ui/Chip';
import { EmptyNote } from '../ui/EmptyNote';
import { PanelSection } from '../ui/PanelSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { RoomSheet } from '../sheets/RoomSheet';
import { RoomLoadsSheet } from '../sheets/RoomLoadsSheet';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import type { Room } from '../../../lib/rcu/model';
import { isWired } from '../../../lib/rcu/model';
import { loadsInRoom } from '../../../lib/rcu/query/grouping';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

const IconAction: React.FC<{
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, danger = false, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] ${
      danger ? 'hover:text-[#F44336]' : 'hover:text-[#D4A054]'
    }`}
  >
    {children}
  </button>
);

/** Rooms, which is what the guest controls page groups loads by. */
export const RoomsPanel: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const setDefaultRoom = useRcuConfigStore((state) => state.setDefaultRoom);
  const unassignRoom = useRcuConfigStore((state) => state.unassignRoom);
  const removeRoom = useRcuConfigStore((state) => state.removeRoom);
  const report = useCommandFeedback();

  const [editing, setEditing] = React.useState<{ room: Room | null } | null>(null);
  const [assigning, setAssigning] = React.useState<Room | null>(null);
  const [removing, setRemoving] = React.useState<Room | null>(null);

  return (
    <div className="space-y-4">
      <PanelSection
        title="Rooms"
        description="Guests see one tab per room. Loads without a room appear under Unassigned."
        action={
          <Button variant="primary" size="sm" onClick={() => setEditing({ room: null })}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add room
          </Button>
        }
      >
        {config.rooms.length === 0 ? (
          <EmptyNote
            message="No rooms yet. Wired loads stay controllable under the Unassigned tab."
            action={
              <Button variant="secondary" size="sm" onClick={() => setEditing({ room: null })}>
                Add the first room
              </Button>
            }
          />
        ) : (
          <ul className="space-y-2">
            {config.rooms.map((room) => {
              const wired = loadsInRoom(config, room.id).filter(isWired);
              const isDefault = config.defaultRoomId === room.id;
              return (
                <li
                  key={room.id}
                  className="rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium text-[#333333]">
                        <span className="truncate">{room.name}</span>
                        {isDefault && <Chip label="This tablet" tone={TONE.SUCCESS} />}
                      </p>
                      <p className="mt-0.5 text-xs text-[#666666]">
                        {room.note.trim() === '' ? 'No note' : room.note} · {wired.length} controllable
                        load(s)
                      </p>
                      {wired.length > 0 && (
                        <p className="mt-1.5 truncate text-xs text-[#333333]">
                          {wired.map((load) => load.name).join(' · ')}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center">
                      <IconAction label="Add loads" onClick={() => setAssigning(room)}>
                        <UserPlus className="h-4 w-4" />
                      </IconAction>
                      {!isDefault && (
                        <IconAction
                          label="Make this the tablet's room"
                          onClick={() =>
                            report(setDefaultRoom(room.id), `${room.name} is now this tablet's room.`)
                          }
                        >
                          <TabletSmartphone className="h-4 w-4" />
                        </IconAction>
                      )}
                      <IconAction
                        label="Unassign all loads"
                        onClick={() =>
                          report(unassignRoom(room.id), `Loads unassigned from ${room.name}.`)
                        }
                      >
                        <Unplug className="h-4 w-4" />
                      </IconAction>
                      <IconAction label="Edit room" onClick={() => setEditing({ room })}>
                        <Pencil className="h-4 w-4" />
                      </IconAction>
                      <IconAction label="Remove room" danger onClick={() => setRemoving(room)}>
                        <Trash2 className="h-4 w-4" />
                      </IconAction>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PanelSection>

      {editing !== null && (
        <RoomSheet isOpen room={editing.room} onClose={() => setEditing(null)} />
      )}

      {assigning !== null && (
        <RoomLoadsSheet isOpen room={assigning} onClose={() => setAssigning(null)} />
      )}

      <ConfirmDialog
        isOpen={removing !== null}
        title="Remove room"
        message={
          removing === null
            ? ''
            : `${removing.name} will be removed. Its loads are kept and become unassigned.`
        }
        confirmLabel="Remove room"
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing === null) return;
          report(removeRoom(removing.id), 'Room removed.');
          setRemoving(null);
        }}
      />
    </div>
  );
};

export default RoomsPanel;
